import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { MessageSquare, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/App';
import { toast } from 'sonner';

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(searchParams.get('mode') !== 'signup');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const { login, signup } = useAuth();
  const navigate = useNavigate();

  // Email validation function
  const validateEmail = (emailInput) => {
    const trimmedEmail = emailInput.trim().toLowerCase();
    // Standard email regex pattern
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(trimmedEmail);
  };

  // Handle email input change with validation
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    // Clear error when user starts typing
    if (emailError) setEmailError('');
  };

  useEffect(() => {
    if (searchParams.get('mode') === 'signup') {
      setIsLogin(false);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Trim and normalize email
    const normalizedEmail = email.trim().toLowerCase();
    
    // Validate email format
    if (!validateEmail(normalizedEmail)) {
      setEmailError('Please enter a valid email address');
      toast.error('Please enter a valid email address');
      return;
    }
    
    setLoading(true);

    try {
      if (isLogin) {
        await login(normalizedEmail, password);
        toast.success('Welcome back!');
      } else {
        if (!name.trim()) {
          toast.error('Please enter your name');
          setLoading(false);
          return;
        }
        await signup(name.trim(), normalizedEmail, password);
        toast.success('Account created successfully!');
      }
      navigate('/');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-xl flex items-center justify-center shadow-md">
              <MessageSquare className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-bold text-2xl text-gray-900" style={{ fontFamily: 'DM Sans' }}>UniConnect</span>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'DM Sans' }}>
            {isLogin ? 'Welcome back' : 'Create account'}
          </h1>
          <p className="text-gray-600 mb-4">
            {isLogin ? 'Sign in to your account' : 'Sign up to get started'}
          </p>

          {/* Toggle between Sign In and Sign Up */}
          <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
            <button
              type="button"
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${
                !isLogin 
                  ? 'bg-white text-indigo-600 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Sign Up
            </button>
            <button
              type="button"
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${
                isLogin 
                  ? 'bg-white text-indigo-600 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Sign In
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-gray-700 mb-2">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="h-11 rounded-xl border-gray-200 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
                  required={!isLogin}
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-sm font-medium text-gray-700 mb-2">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                onBlur={() => {
                  if (email && !validateEmail(email.trim())) {
                    setEmailError('Please enter a valid email address');
                  }
                }}
                placeholder="john@torontomu.ca"
                className={`h-11 rounded-xl border-gray-200 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 ${
                  emailError ? 'border-red-400 focus:border-red-400 focus:ring-red-100' : ''
                }`}
                required
              />
              {emailError ? (
                <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                  <span className="inline-block w-1 h-1 bg-red-500 rounded-full"></span>
                  {emailError}
                </p>
              ) : (
                <p className="text-xs text-blue-600 mt-1.5 flex items-center gap-1">
                  <span className="inline-block w-1 h-1 bg-blue-500 rounded-full"></span>
                  Recommended: Use your TMU email to connect with classmates
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-gray-700 mb-2">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 rounded-xl border-gray-200 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 pr-10"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Checkbox 
                  id="show-password"
                  checked={showPassword}
                  onCheckedChange={setShowPassword}
                  className="h-4 w-4 rounded border-gray-300 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                />
                <label 
                  htmlFor="show-password" 
                  className="text-xs text-gray-500 cursor-pointer select-none"
                >
                  Show password
                </label>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-50"
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Sign Up')}
            </Button>

            <p className="text-xs text-center text-gray-500 mt-3">
              By signing up, you agree to our{' '}
              <Link to="/terms" className="text-indigo-600 hover:underline">Terms of Use</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-indigo-600 hover:underline">Privacy Policy</Link>.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}